import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "../../styles/components/forms.css";
import "../../styles/user/login.css";
import axios from "axios";
import { getRecaptchaToken, loadRecaptcha } from "../../utils/recaptcha";
import { tryOpenAppThenStore, APP_STORE_URL, PLAY_STORE_URL } from "../../utils/appStoreLinks";

const Login = () => {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [urlDiscountCode, setUrlDiscountCode] = useState("");
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeFromUrl = params.get("discount_code");
    if (codeFromUrl && codeFromUrl.trim() !== "") {
      const normalized = codeFromUrl
        .trim()
        .replace(/^"+|"+$/g, "")
        .toUpperCase();
      setUrlDiscountCode(normalized);
      sessionStorage.setItem("pendingDiscountCode", normalized);
      localStorage.setItem("pendingDiscountCode", normalized);
    }
  }, [location.search]);

  useEffect(() => {
    const trialWarning = localStorage.getItem('trialDiscountWarning');
    if (trialWarning) {
      setError(trialWarning);
      localStorage.removeItem('trialDiscountWarning');
    }
  }, []);

  useEffect(() => {
    // Wait for loading to complete, then redirect if user is authenticated
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
    
    // Pre-load reCAPTCHA script when component mounts
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      loadRecaptcha().catch(err => {
        console.warn('reCAPTCHA pre-load failed:', err);
      });
    }
  }, [user, loading, navigate]);

  const openApp = (e) => {
    e.preventDefault();
    tryOpenAppThenStore("lopeglad://");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get reCAPTCHA token (optional for login, but good for security)
      const recaptchaToken = await getRecaptchaToken('login');
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
        {
          ...credentials,
          discountCode:
            urlDiscountCode ||
            sessionStorage.getItem("pendingDiscountCode") ||
            localStorage.getItem("pendingDiscountCode") ||
            undefined,
          recaptchaToken: recaptchaToken || undefined // Only send if available
        },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.success) {
        setSuccess(data.message);
        setError("");
        // Let AuthContext handle the user state
        await login(data.user);
        navigate("/dashboard", { replace: true });
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Innlogging feilet");
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Laster...</p>
      </div>
    );
  }

  // If user is already authenticated, show loading while redirecting
  if (user) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Omdirigerer til dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="login-container">
        <form onSubmit={handleSubmit} className="login-form">
          <h2>Logg inn</h2>
          {urlDiscountCode && (
            <div className="success-message">Rabattkode fra lenke: {urlDiscountCode}</div>
          )}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <input
            type="email"
            placeholder="Epost"
            value={credentials.email}
            onChange={(e) =>
              setCredentials({ ...credentials, email: e.target.value })
            }
          />
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Passord"
              value={credentials.password}
              onChange={(e) =>
                setCredentials({ ...credentials, password: e.target.value })
              }
            />
            <span
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={showPassword ? "Skjul passord" : "Vis passord"}
              title={showPassword ? "Skjul passord" : "Vis passord"}
            >
              {showPassword ? (
                <span aria-hidden="true">👁️</span>
              ) : (
                <span aria-hidden="true">👁️‍🗨️</span>
              )}
            </span>
          </div>
          <div className="forgot-password-link">
            <Link to="/forgot-password">Glemt passord?</Link>
          </div>
          <button type="submit">Logg inn</button>
          <button 
            type="button"
            onClick={openApp}
            className="open-app-button"
          >
            Åpne i appen
          </button>
          <p className="store-links-hint">
            Har du ikke appen? Last ned på{' '}
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">App Store</a>
            {' · '}
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>
          </p>
          <Link className="create-account-link" to="/create-account">
            Opprett konto
          </Link>
          {/* reCAPTCHA v3 badge - required by Google */}
          {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
            <div className="recaptcha-notice">
              <small>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                {' '}and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                {' '}apply.
              </small>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
